import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/axios";
import { InvoicePositionPercent } from "../../types/invoice";
import { colors, spacing, borderRadius, typography, styles, createButtonStyle, shadows } from "../../styles/theme";

type AllegroClient = { id: string; nickname: string };
type GoogleAdsClient = {
  id: number;
  customer_id: string;
  descriptive_name: string;
  mcc_label: string;
  currency_code: string;
};
type Client = AllegroClient | GoogleAdsClient;
type SourceType = 'allegro' | 'google_ads';

type Props = { onAdd: (pos: InvoicePositionPercent) => void; onCancel: () => void; };
type MissingDaysResponse = string[] | { missing: string[] };

/** --- helpers: formatowanie dat po lokalnemu (bez UTC shift) --- */
function pad2(n: number) { return n < 10 ? `0${n}` : String(n); }
function fmt(d: Date) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }

function addDaysLocal(iso: string, days: number) {
  const [y, m, dd] = iso.split("-").map(Number);
  const d = new Date(y, m - 1, dd);
  d.setHours(12, 0, 0, 0);           // bezpiecznik na DST
  d.setDate(d.getDate() + days);
  return fmt(d);
}

function lastFullMonthKey(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();          // 0..11; m==0 => styczeń -> poprzedni to grudzień poprzedniego roku
  const prevY = m === 0 ? y - 1 : y;
  const prevM = m === 0 ? 12 : m;    // 1..12
  return `${prevY}-${pad2(prevM)}`;
}

function ymToRange(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  const start = new Date(y, m - 1, 1);
  const end   = new Date(y, m, 0);
  return { date_from: fmt(start), date_to: fmt(end) };
}

const PercentForm = ({ onAdd, onCancel }: Props) => {
  // --- Source type (Allegro / Google Ads) ---
  const [sourceType, setSourceType] = useState<SourceType>('allegro');

  // --- Client search (generic for both sources) ---
  const [clientSearch, setClientSearch] = useState("");
  const [clientOptions, setClientOptions] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // --- tryb daty ---
  const [mode, setMode] = useState<"range" | "previous_month">("range");
  const [dateFrom, setDateFrom] = useState("");
  const dateTo = useMemo(() => (dateFrom ? addDaysLocal(dateFrom, 30) : ""), [dateFrom]);

  // --- poprzedni miesiąc: input type="month" ---
  const [monthKey, setMonthKey] = useState<string>(lastFullMonthKey());

  // --- parametry pozycji ---
  const [percent, setPercent] = useState<number>(0);
  const [description, setDescription] = useState("");
  const [vat, setVat] = useState<number>(23);
  const [threshold, setThreshold] = useState<number>(0);
  const [capEnabled, setCapEnabled] = useState<boolean>(false);
  const [capAmount, setCapAmount] = useState<number>(0);
  const [gtuCode, setGtuCode] = useState<string>("GTU_12");

  // --- edytowalna NAZWA pozycji ---
  const defaultName = useMemo(
    () => threshold === 0
      ? `${percent}% od całego budżetu`
      : `${percent}% od nadwyżki powyżej ${Math.round(threshold)} PLN`,
    [percent, threshold]
  );
  const [name, setName] = useState<string>(defaultName);
  const [nameDirty, setNameDirty] = useState(false);
  useEffect(() => { if (!nameDirty) setName(defaultName); }, [defaultName, nameDirty]);

  // --- walidacje/komunikaty ---
  const [error, setError] = useState("");
  const [warning, setWarning] = useState(""); 
  const [checkingSubmit, setCheckingSubmit] = useState(false);
  const [checkingMissing, setCheckingMissing] = useState(false);
  const [missingDays, setMissingDays] = useState<string[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef<AbortController | null>(null);

  const isValidBasics =
    !!selectedClient &&
    percent >= 0 &&
    (mode === "range" ? !!dateFrom : !!monthKey);

  const disableSubmit =
    !isValidBasics || checkingSubmit || checkingMissing || !!error;

  // odczyt preferencji z localStorage
  useEffect(() => {
    const dp = (localStorage.getItem("builder_date_policy") as "previous_month" | "rolling_30" | null) || null;
    const anchor = localStorage.getItem("builder_anchor_start") || "";
    if (dp === "previous_month") {
      setMode("previous_month");
      setMonthKey(lastFullMonthKey());
    } else if (dp === "rolling_30") {
      setMode("range");
      if (anchor) setDateFrom(anchor);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setClientOptions([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setError("");
    setWarning("");
    setMissingDays([]);
  }, [mode]);

  useEffect(() => {
    if (mode === "range" && dateFrom) {
      localStorage.setItem("builder_anchor_start", dateFrom);
    }
  }, [mode, dateFrom]);

  // --- live search (Allegro / Google Ads) ---
  const handleClientSearch = async (value: string) => {
    setClientSearch(value);
    setSelectedClient(null);
    setError("");
    setWarning("");
    setMissingDays([]);

    if (value.length < 2) {
      setClientOptions([]);
      return;
    }

    try {
      let resp;
      if (sourceType === 'allegro') {
        resp = await api.get("/allegro-clients", { params: { nickname: value } });
      } else {
        resp = await api.get("/google-ads/customers", { params: { search: value } });
      }
      setClientOptions(Array.isArray(resp.data) ? resp.data : []);
    } catch {
      setClientOptions([]);
      setError(`Błąd wyszukiwania klientów ${sourceType === 'allegro' ? 'Allegro' : 'Google Ads'}.`);
    }
  };

  const handleSelectClient = (client: Client) => {
    setSelectedClient(client);

    // Display name based on source
    if (sourceType === 'allegro') {
      setClientSearch((client as AllegroClient).nickname);
    } else {
      const googleClient = client as GoogleAdsClient;
      setClientSearch(googleClient.descriptive_name || googleClient.customer_id);
    }

    setClientOptions([]);
    setError("");
    setWarning("");
  };

  // --- missing days dla rollingu ---
  useEffect(() => {
    if (mode !== "range") { setMissingDays([]); return; }
    if (!selectedClient || !dateFrom || !dateTo) { setMissingDays([]); return; }
    setError("");
    setWarning("");

    const run = async () => {
      try {
        inFlight.current?.abort();
        const ctrl = new AbortController();
        inFlight.current = ctrl;

        setCheckingMissing(true);

        let resp;
        if (sourceType === 'allegro') {
          resp = await api.get<MissingDaysResponse>("/missing-days", {
            params: { client_id: (selectedClient as AllegroClient).id, date_from: dateFrom, date_to: dateTo },
            signal: ctrl.signal,
          });
        } else {
          resp = await api.get<MissingDaysResponse>("/google-ads/missing-days", {
            params: { customer_id: (selectedClient as GoogleAdsClient).customer_id, date_from: dateFrom, date_to: dateTo },
            signal: ctrl.signal,
          });
        }

        const payload = resp.data;
        const days = Array.isArray(payload) ? payload : (payload.missing ?? []);
        setMissingDays(days);

        if (days.length) {
          const head = days.slice(0, 6).join(", ");
          const tail = days.length > 6 ? ` … (+${days.length - 6})` : "";
          setWarning(`Uwaga: w tym okresie brakuje danych/kosztów dla dni: ${head}${tail}`);
        } else {
          setWarning("");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Błąd podczas sprawdzania brakujących dni.");
        setMissingDays([]);
        setWarning("");
      } finally {
        setCheckingMissing(false);
      }
    };

    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [mode, selectedClient, dateFrom, dateTo, sourceType]);

    // --- missing days dla poprzedniego miesiąca ---
  useEffect(() => {
    if (mode !== "previous_month") { return; }
    if (!selectedClient || !monthKey) { setMissingDays([]); return; }
    setError("");
    setWarning("");

    const run = async () => {
      try {
        inFlight.current?.abort();
        const ctrl = new AbortController();
        inFlight.current = ctrl;

        setCheckingMissing(true);
        const { date_from, date_to } = ymToRange(monthKey);

        let resp;
        if (sourceType === 'allegro') {
          resp = await api.get<MissingDaysResponse>("/missing-days", {
            params: { client_id: (selectedClient as AllegroClient).id, date_from, date_to },
            signal: ctrl.signal,
          });
        } else {
          resp = await api.get<MissingDaysResponse>("/google-ads/missing-days", {
            params: { customer_id: (selectedClient as GoogleAdsClient).customer_id, date_from, date_to },
            signal: ctrl.signal,
          });
        }

        const payload = resp.data;
        const days = Array.isArray(payload) ? payload : (payload.missing ?? []);
        setMissingDays(days);

        if (days.length) {
          const head = days.slice(0, 6).join(", ");
          const tail = days.length > 6 ? ` … (+${days.length - 6})` : "";
          setWarning(`Uwaga: w tym okresie brakuje danych/kosztów dla dni: ${head}${tail}`);
        } else {
          setWarning("");
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Błąd podczas sprawdzania brakujących dni.");
        setMissingDays([]);
        setWarning("");
      } finally {
        setCheckingMissing(false);
      }
    };

    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
  }, [mode, selectedClient, monthKey, sourceType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!isValidBasics) {
      setError("Uzupełnij wymagane pola.");
      return;
    }
    if ((mode === "range" || mode === "previous_month") && missingDays.length > 0) {
      const msg = `W wybranym okresie brakuje danych/kosztów dla dni:\n- ${missingDays.join(
        "\n- "
      )}\n\nCzy chcesz kontynuować mimo to?`;
      const ok = window.confirm(msg);
      if (!ok) return;
    }
    if (threshold < 0) {
      setError("Próg (PLN) nie może być ujemny.");
      return;
    }

    try {
      setCheckingSubmit(true);

      let df = "", dt = "", policy: "rolling_30" | "previous_month";
      if (mode === "range") {
        df = dateFrom!;
        dt = dateTo!;
        policy = "rolling_30";
        localStorage.setItem("builder_date_policy", "rolling_30");
        localStorage.setItem("builder_anchor_start", df);
      } else {
        const { date_from, date_to } = ymToRange(monthKey);
        df = date_from; dt = date_to;
        policy = "previous_month";
        localStorage.setItem("builder_date_policy", "previous_month");
        localStorage.setItem("builder_anchor_start", "");
      }

      // Prepare client data based on source
      let clientData: any = {};
      if (sourceType === 'allegro') {
        const allegroClient = selectedClient as AllegroClient;
        clientData = {
          nickname: allegroClient.nickname,
          client_id: allegroClient.id,
        };
      } else {
        const googleClient = selectedClient as GoogleAdsClient;
        clientData = {
          nickname: googleClient.descriptive_name || googleClient.customer_id,
          client_id: googleClient.customer_id, // customer_id dla Google Ads
        };
      }

      onAdd({
        type: "percent",
        name,
        ...clientData,
        date_from: df,
        date_to: dt,
        percent,
        amount_netto: 0,
        cost: 0,
        vat,
        description,
        policy,
        anchor_start: policy === "rolling_30" ? df : undefined,
        month_key: policy === "previous_month" ? monthKey : undefined,
        threshold,
        cap_enabled: capEnabled,
        cap_amount: capEnabled ? capAmount : undefined,
        source_type: sourceType,
        gtu_code: gtuCode || null,
      } as any);
    } finally {
      setCheckingSubmit(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{
      padding: spacing.lg,
      background: colors.backgroundAlt,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{
        marginBottom: spacing.md,
        padding: spacing.md,
        background: colors.primaryLight,
        borderLeft: `3px solid ${colors.primary}`,
        borderRadius: borderRadius.sm,
      }}>
        <strong style={{ fontSize: typography.fontSize.base }}>Pozycja: Procent</strong>
        <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, marginTop: spacing.xs }}>
          Kwota = (koszt - próg) * procent / 100
        </div>
      </div>

      {/* Source Selector */}
      <div style={{ marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Źródło danych:
          </span>
          <select
            value={sourceType}
            onChange={e => {
              setSourceType(e.target.value as SourceType);
              setSelectedClient(null);
              setClientSearch("");
              setClientOptions([]);
              setError("");
              setWarning("");
              setMissingDays([]);
            }}
            style={styles.select}
          >
            <option value="allegro">Allegro Ads</option>
            <option value="google_ads">Google Ads</option>
          </select>
        </label>
      </div>

      {/* Client Search */}
      <div ref={wrapperRef} style={{ position: "relative", marginBottom: spacing.lg }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Klient {sourceType === 'allegro' ? 'Allegro' : 'Google Ads'}:
          </span>
          <input
            value={clientSearch}
            onChange={e => handleClientSearch(e.target.value)}
            placeholder={sourceType === 'allegro' ? "Wpisz nick (min 2 znaki)" : "Wpisz nazwę klienta (min 2 znaki)"}
            autoComplete="off"
            style={{ ...styles.input, width: "100%" }}
          />
        </label>
        {clientOptions.length > 0 && (
          <ul style={{
            position: "absolute", top: "100%", left: 0, background: colors.background,
            border: `1px solid ${colors.border}`, zIndex: 1000, listStyle: "none",
            margin: `${spacing.xs}px 0 0 0`, padding: 0, width: "100%", maxHeight: 180, overflowY: "auto",
            boxShadow: shadows.lg, borderRadius: borderRadius.sm
          }}>
            {clientOptions.map(c => {
              const isAllegro = sourceType === 'allegro';
              const displayText = isAllegro
                ? `${(c as AllegroClient).nickname} (${c.id})`
                : `${(c as GoogleAdsClient).descriptive_name || (c as GoogleAdsClient).customer_id} (${(c as GoogleAdsClient).customer_id})`;

              return (
                <li key={isAllegro ? c.id : (c as GoogleAdsClient).customer_id}
                    style={{
                      padding: spacing.md,
                      cursor: "pointer",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      transition: "background 0.2s ease"
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = colors.primaryLight; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    onClick={() => handleSelectClient(c)}>
                  {displayText}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div style={{
        marginBottom: spacing.md,
        padding: spacing.md,
        background: colors.gray50,
        borderRadius: borderRadius.sm,
        display: "flex",
        flexDirection: "column",
        gap: spacing.sm
      }}>
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
          Tryb daty:
        </span>
        <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
          <input
            type="radio"
            name="percent-mode"
            checked={mode === "range"}
            onChange={() => {
              setMode("range");
              setError(""); setMissingDays([]);
              localStorage.setItem("builder_date_policy", "rolling_30");
              if (dateFrom) localStorage.setItem("builder_anchor_start", dateFrom);
            }}
          />
          <span style={{ fontSize: typography.fontSize.sm }}>
            Umowy miesięczne (data od +30 dni, 31 dni łącznie)
          </span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer" }}>
          <input
            type="radio"
            name="percent-mode"
            checked={mode === "previous_month"}
            onChange={() => {
              setMode("previous_month");
              setError(""); setMissingDays([]);
              localStorage.setItem("builder_date_policy", "previous_month");
              localStorage.setItem("builder_anchor_start", "");
              setMonthKey(lastFullMonthKey());
            }}
          />
          <span style={{ fontSize: typography.fontSize.sm }}>
            Nieokreślony (ostatni pełny miesiąc)
          </span>
        </label>
      </div>

      {mode === "range" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
              Data od:
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              disabled={!selectedClient}
              style={styles.input}
            />
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
              Data do (auto):
            </span>
            <div style={{
              ...styles.input,
              background: colors.gray100,
              display: "flex",
              alignItems: "center",
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary
            }}>
              {dateTo || "—"}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: spacing.md }}>
          <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
              Miesiąc:
            </span>
            <input
              type="month"
              value={monthKey}
              onChange={e => setMonthKey(e.target.value)}
              disabled={!selectedClient}
              style={styles.input}
            />
          </label>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: spacing.md, marginBottom: spacing.md }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Nazwa pozycji:
          </span>
          <input
            value={name}
            onChange={e => { setName(e.target.value); setNameDirty(true); }}
            placeholder={defaultName}
            style={styles.input}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Procent (%):
          </span>
          <input
            type="number"
            step="0.1"
            value={percent}
            min={0}
            max={100}
            onChange={e => setPercent(Number(e.target.value))}
            style={styles.input}
          />
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: spacing.md, marginBottom: spacing.lg }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            VAT (%):
          </span>
          <select value={vat} onChange={e => setVat(Number(e.target.value))} style={styles.select}>
            <option value={23}>23%</option>
            <option value={8}>8%</option>
            <option value={0}>0%</option>
            <option value={5}>5%</option>
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            GTU:
          </span>
          <select value={gtuCode} onChange={e => setGtuCode(e.target.value)} style={styles.select}>
            <option value="GTU_12">GTU_12</option>
            <option value="">Brak</option>
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Próg (PLN):
          </span>
          <input
            type="number"
            step="0.01"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            style={styles.input}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Opis (opcjonalny):
          </span>
          <input
            placeholder="Tylko wewnętrznie"
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            style={styles.input}
          />
        </label>
      </div>

      {/* Cap (limit nadwyżki) */}
      <div style={{
        marginBottom: spacing.lg,
        padding: spacing.md,
        background: colors.gray50,
        borderRadius: borderRadius.sm,
        border: `1px solid ${colors.border}`
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer", marginBottom: capEnabled ? spacing.md : 0 }}>
          <input
            type="checkbox"
            checked={capEnabled}
            onChange={e => setCapEnabled(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
            Blokada nadwyżki (cap)
          </span>
        </label>
        {capEnabled && (
          <div style={{ marginTop: spacing.sm }}>
            <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }}>
                Maksymalna kwota netto (PLN):
              </span>
              <input
                type="number"
                step="0.01"
                min={0}
                value={capAmount}
                onChange={e => setCapAmount(Number(e.target.value))}
                placeholder="Wpisz maksymalną kwotę"
                style={styles.input}
              />
            </label>
            <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
              Jeśli obliczona kwota przekroczy limit, zostanie obcięta do tej wartości.
            </div>
          </div>
        )}
      </div>

      {warning && (
        <div style={{
          padding: spacing.md,
          background: colors.warningLight,
          border: `1px solid ${colors.warning}`,
          borderRadius: borderRadius.sm,
          color: colors.warningDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          ⚠️ {warning}
        </div>
      )}

      {error && (
        <div style={{
          padding: spacing.md,
          background: colors.errorLight,
          border: `1px solid ${colors.error}`,
          borderRadius: borderRadius.sm,
          color: colors.errorDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          ❌ {error}
        </div>
      )}

      {checkingMissing && (
        <div style={{
          padding: spacing.sm,
          background: colors.infoLight,
          borderRadius: borderRadius.sm,
          color: colors.infoDark,
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm
        }}>
          🔍 Sprawdzam brakujące dni…
        </div>
      )}

      <div style={{ display: "flex", gap: spacing.md }}>
        <button type="submit" disabled={disableSubmit} style={createButtonStyle('primary', disableSubmit)}>
          {checkingSubmit ? "⏳ Sprawdzam…" : "✅ Dodaj pozycję"}
        </button>
        <button type="button" onClick={onCancel} style={createButtonStyle('secondary')}>
          ❌ Anuluj
        </button>
      </div>
    </form>
  );
};

export default PercentForm;
